/**
 * 🔴 QUEUE N2b-2 — THE PRINTED WORKOUT, AS EVERFIT'S GRID (his word 2026-10-07 ~04:10: *"why the pdf looks like a bunch of
 * text? Look how Everfit does it, and how pro it looks"*; their print is `docs/reference/everfit-print-workout-20261006.pdf`).
 *
 * The TypeScript twin of `HybridTrainer/Services/WorkoutPrintout.swift`, FIELD FOR FIELD — one model, two renderers (the
 * phone's `WorkoutPrintRenderer`, the web's `WorkoutPrintSheet`). What each side proves:
 *   - a cell's words: `cellText(phoneDose([row]))` against the Swift's `cellText(CoachSetDose([row]).overviewLine)` over
 *     every case of the dose corpus (`__fixtures__/doseCorpus.json`'s `cells`, `workoutPrintout.test.ts`);
 *   - the format words, the timed formats and the box rule: the harness reads this file (gate `n2b-print-workout`).
 * The web reads the COACH's set rows, so each cell carries its own rest; the phone holds one rest per exercise.
 */

import type { AssignedWorkoutTree, TreeSection } from "./assignedWorkoutTree";
import { formatHeadline, readFormatConfig, setLabelOf, clock, type SectionFormat } from "./builder";
import { hasRealName, phoneSectionName } from "./briefing";
import { phoneDose } from "./phoneDose";
import { UNNAMED_EXERCISE, byPosition, setRows, type FieldsById } from "./workoutPreview";
import { equipmentChips } from "./phoneOverview";

export type PrintCell = { label: string; text: string; rest: string };

export type PrintRow = {
  /** `1.`, `2.` — its place in its box; empty for a loose exercise. */
  number: string;
  name: string;
  cells: PrintCell[];
  note: string;
  /** Which superset run of its box the row belongs to; null = on its own. */
  superset: number | null;
};

export type PrintKind = "section" | "superset" | "single";

export type PrintBlock = {
  kind: PrintKind;
  title: string;
  format: string;
  figure: string;
  note: string;
  timed: boolean;
  rows: PrintRow[];
};

export type WorkoutPrintout = {
  title: string;
  client: string;
  date: string;
  description: string;
  equipment: string[];
  blocks: PrintBlock[];
};

/** `WorkoutPrintout.cellText`: the one-set sentence without its count; `1 set` (a set stating nothing) is an empty cell. */
export function cellText(oneSetLine: string): string {
  if (oneSetLine === "1 set") return "";
  return oneSetLine.startsWith("1 × ") ? oneSetLine.slice(4) : oneSetLine;
}

/** `WorkoutPrintout.restText`: the builder's `clock` (`45s`, `1:30`), empty for none. */
export function restText(seconds: number | null | undefined): string {
  return seconds === null || seconds === undefined || seconds <= 0 ? "" : clock(seconds);
}

/** `WorkoutPrintout.formatWords` — the phone's `SectionFormat.displayName` (the owner named `Timed`). */
export const PRINT_FORMAT_WORDS: Record<SectionFormat, string> = {
  straight_sets: "Regular", superset: "Superset", circuit: "Circuit", emom: "EMOM",
  amrap: "AMRAP", timed_interval: "Interval", for_time: "Timed",
};
export function formatWord(format: string): string {
  return (PRINT_FORMAT_WORDS as Record<string, string | undefined>)[format] ?? "Regular";
}

export const TIMED_FORMATS: readonly string[] = ["circuit", "emom", "amrap", "timed_interval", "for_time"];
export function isTimed(format: string): boolean { return TIMED_FORMATS.includes(format); }

/** `WorkoutPrintout.rowNote`: the tempo, then the coach's note. */
export function rowNote(tempo: string | null, note: string | null): string {
  const t = (tempo ?? "").trim(), n = (note ?? "").trim();
  return [t === "" ? "" : `Tempo ${t}`, n].filter((x) => x !== "").join(" · ");
}

export function setsWord(n: number): string { return n === 1 ? "1 Set" : `${String(n)} Sets`; }

/** A timed row's one value: the set's words when every set says the same, else each in order. */
export function rowValue(r: PrintRow): string {
  const texts = r.cells.map((c) => c.text);
  if (texts.length === 0) return "";
  return texts.every((t) => t === texts[0]) ? texts[0]! : texts.join(" / ");
}
/** A timed row's REST column: the rest when every set has the same one, else nothing. */
export function rowRest(r: PrintRow): string {
  const rests = r.cells.map((c) => c.rest);
  return rests.length > 0 && rests.every((x) => x === rests[0]) ? rests[0]! : "";
}
/** The tab's or the strip's set count — the smaller count wins (his unequal-superset rule). */
export function supersetSets(b: PrintBlock, run: number | null): number {
  const counts = b.rows.filter((r) => run === null || r.superset === run).map((r) => r.cells.length);
  return counts.length === 0 ? 0 : Math.min(...counts);
}

export type SectionInput = { heading: string | null; format: string; figure: string; note: string; rows: RowInput[] };
export type RowInput = { group: string | null; name: string; cells: PrintCell[]; note: string };

/** `WorkoutPrintout.runs`: each ADJACENT stretch sharing a group, as [start, end). */
export function runs(groups: readonly (string | null)[]): [number, number][] {
  const out: [number, number][] = [];
  let i = 0;
  while (i < groups.length) {
    let j = i + 1;
    const g = groups[i];
    if (g !== null && g !== undefined) while (j < groups.length && groups[j] === g) j += 1;
    out.push([i, j]);
    i = j;
  }
  return out;
}

/** `WorkoutPrintout.blocks` — THE ONE RULE from sections to boxes (see the Swift for the reasons). */
export function printBlocks(sections: readonly SectionInput[]): PrintBlock[] {
  const out: PrintBlock[] = [];
  for (const s of sections) {
    if (s.rows.length === 0) continue;
    const rs = runs(s.rows.map((r) => r.group));
    if (s.heading !== null) {
      const rows: PrintRow[] = [];
      let run = 0;
      for (const [a, b] of rs) {
        const isSuperset = b - a >= 2;
        for (let k = a; k < b; k++) {
          const x = s.rows[k]!;
          rows.push({ number: `${String(k + 1)}.`, name: x.name, cells: x.cells, note: x.note, superset: isSuperset ? run : null });
        }
        if (isSuperset) run += 1;
      }
      out.push({ kind: "section", title: s.heading, format: formatWord(s.format), figure: s.figure, note: s.note,
                 timed: isTimed(s.format), rows });
    } else {
      rs.forEach(([a, b], n) => {
        const isSuperset = b - a >= 2;
        const rows: PrintRow[] = [];
        for (let k = a; k < b; k++) {
          const x = s.rows[k]!;
          rows.push({ number: isSuperset ? `${String(k - a + 1)}.` : "", name: x.name, cells: x.cells, note: x.note,
                      superset: isSuperset ? 0 : null });
        }
        out.push({ kind: isSuperset ? "superset" : "single", title: "", format: "", figure: "",
                   note: n === 0 ? s.note : "", timed: false, rows });
      });
    }
  }
  return out;
}

/** `Oct 6, 2026` from a `YYYY-MM-DD` day (read as a calendar day, never shifted by a time zone). */
export function printDate(dayISO: string | null, locale?: string): string {
  if (dayISO === null) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dayISO);
  if (m === null) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" });
}

/**
 * One workout's page from the tree the week already read (`useWeekTrees`) — the sets in the coach's order, each cell the
 * phone's one-set sentence (`phoneDose` over that one row), each label the builder's set-column rule (`setLabelOf`).
 * Heading by the phone's rule (`phoneSectionName` → `hasRealName`), figure by the builder's `formatHeadline`.
 */
export function printoutOf(tree: AssignedWorkoutTree, opts: {
  client: string; dateISO: string | null; equipment: readonly string[]; fields?: FieldsById; locale?: string;
}): WorkoutPrintout {
  const sections = byPosition(tree.coach_workout_sections ?? []).map((sec, si) => sectionInput(sec, si, opts.fields));
  return {
    title: tree.name,
    client: opts.client,
    date: printDate(opts.dateISO, opts.locale),
    description: (tree.description ?? "").trim(),
    equipment: [...opts.equipment],
    blocks: printBlocks(sections),
  };
}

function sectionInput(sec: TreeSection, si: number, fields?: FieldsById): SectionInput {
  const implicit = sec.is_implicit === true;
  const format = sec.format ?? "straight_sets";
  const raw = (sec.name ?? "").trim();
  const name = phoneSectionName(raw === "" ? null : raw, format, implicit);
  const exercises = byPosition(sec.coach_workout_exercises ?? []);
  // A run of `superset_with_next` rows is one group (the phone's `formatGroupId`): a row CONTINUES the group of the row
  // before it when that row says so, otherwise it opens its own (a group of one is a loose row, as on the phone).
  let group = -1;
  const groups = exercises.map((_, i) => {
    if (i === 0 || exercises[i - 1]!.superset_with_next !== true) group += 1;
    return `${String(si)}-${String(group)}`;
  });
  return {
    heading: hasRealName(name, implicit) ? name : null,
    format,
    figure: formatHeadline(format as SectionFormat, readFormatConfig(format as SectionFormat, sec.format_config)) ?? "",
    note: (sec.description ?? "").trim(),
    rows: exercises.map((ex, i) => {
      const rows = setRows(ex.coach_workout_sets ?? []);
      const own = ex.exercise_id === null ? null : fields?.get(ex.exercise_id) ?? null;
      const types = rows.map((r) => r.set_type);
      const name = (ex.coach_exercises?.name ?? "").trim();
      return {
        group: groups[i] ?? null,
        name: name === "" ? UNNAMED_EXERCISE : name,
        cells: rows.map((r, k) => ({ label: setLabelOf(types, k), text: cellText(phoneDose([r], own, ex.hold_until_lost === true)), rest: restText(r.rest_seconds) })),
        note: rowNote(ex.tempo, ex.notes),
      };
    }),
  };
}

/** `Upper A` → the document title the browser offers as the PDF's name. */
export function printTitle(printouts: readonly WorkoutPrintout[]): string {
  const first = printouts[0];
  if (first === undefined) return "Workout";
  return printouts.length === 1 ? first.title : `${first.client === "" ? "Week" : first.client} — week`;
}

/**
 * The client week's printout (the calendar's Print, Extended only): every visible day's workouts in order, each its own
 * pages — from the trees the Extended view already read (`useWeekTrees`), so printing reads nothing new. A day's TASK has no
 * workout id here and prints nothing; a workout whose tree did not come back is left out rather than printed blank.
 */
export function weekPrintouts(
  days: readonly { dayKey: string; items: readonly { workoutId?: string }[] }[],
  trees: ReadonlyMap<string, AssignedWorkoutTree>,
  opts: { client: string; equipment?: ReadonlyMap<string, readonly string[]>; fields?: FieldsById; locale?: string },
): WorkoutPrintout[] {
  const out: WorkoutPrintout[] = [];
  for (const d of days) for (const i of d.items) {
    const tree = i.workoutId === undefined ? undefined : trees.get(i.workoutId);
    if (tree === undefined) continue;
    const p = printoutOf(tree, {
      client: opts.client, dateISO: d.dayKey, fields: opts.fields, locale: opts.locale,
      equipment: equipmentChips(tree, opts.equipment ?? new Map()).map((c) => c.label),
    });
    if (p.blocks.some((b) => b.rows.length > 0)) out.push(p);
  }
  return out;
}
